import { useTranslation } from '../presentation/common/useTranslation.js';
import { presentationText } from '../presentation/text';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CheckCircle2 } from 'lucide-react';
import { ExperienceIcon, KarmaIcon } from './RewardIcons';
import ExperienceReward from './ExperienceReward';
import KarmaScoreLink from './KarmaScoreLink';
import { CategoryIcon } from './GameIcon';
import { getNotifications } from '../services/api/notifications';
import { getMyHeroicActions } from '../services/api/heroicActions';
import { getCategoryMilestoneUnlocks } from '../services/api/auth';
import { categoryTitle } from '../utils/categoryMilestones';
import CategoryMilestoneBadge from './CategoryMilestoneBadge';
import './DialogProvider.css';
import './HeroicApprovalPopup.css';

const APPROVAL_TYPES = new Set(['HeroicActionApproved', 'GuildHeroicActionApproved']);

export default function HeroicApprovalPopup({ userId, level, enabled = true, onStateChange }) {
  const { t, i18n } = useTranslation();
  const [queue, setQueue] = useState([]);
  const dialog = useRef(null);
  const active = enabled ? queue[0] : null;

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    let checking = false;
    const check = async () => {
      if (checking || (enabled && queue.length)) return;
      checking = true;
      onStateChange({ level, pending: enabled });
      try {
        const notifications = (await getNotifications(1, 50))?.data || [];
        const candidates = notifications.filter(item => APPROVAL_TYPES.has(item.type) && !item.isRead &&
          item.referenceId && !localStorage.getItem(`goodvale_approval_seen_${userId}_${item.id}`));
        if (!enabled) {
          for (const item of candidates) localStorage.setItem(`goodvale_approval_seen_${userId}_${item.id}`, '1');
          if (mounted) setQueue([]);
          return;
        }
        if (candidates.length) {
          const actions = await getMyHeroicActions({ status: 'Approved' });
          const unlocks = await getCategoryMilestoneUnlocks().catch(() => ({}));
          const matches = candidates.map(notification => ({
            notification,
            action: actions.find(action => String(notification.referenceId).toLowerCase() ===
              String(notification.type === 'GuildHeroicActionApproved' ? action.guildHeroicActionId : action.id).toLowerCase()),
            milestoneLevels: [],
          })).filter(item => item.action).map(item => ({
            ...item, milestoneLevels: unlocks[item.action.id] || [],
          }));
          if (mounted && matches.length) {
            setQueue(matches.reverse());
            onStateChange({ level, pending: true });
            return;
          }
        }
      } catch (error) {
        console.error('Error checking confirmed heroic actions:', error);
      } finally { checking = false; }
      if (mounted) onStateChange({ level, pending: false });
    };
    check();
    const interval = setInterval(check, 30000);
    window.addEventListener('focus', check);
    window.addEventListener('notifications-changed', check);
    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', check);
      window.removeEventListener('notifications-changed', check);
    };
  }, [userId, level, enabled, queue.length, onStateChange]);

  useEffect(() => {
    if (!active) return;
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [active]);

  const close = () => {
    if (!active) return;
    localStorage.setItem(`goodvale_approval_seen_${userId}_${active.notification.id}`, '1');
    const remaining = queue.slice(1);
    setQueue(remaining);
  };

  if (!active) return null;
  const { action, milestoneLevels } = active;
  const xp = action.creditedExperience ?? action.awardedExperience ?? 0;
  const categoryXp = action.creditedCategoryExperience ?? action.awardedCategoryExperience ?? 0;
  const karma = action.creditedKarma ?? (!action.isGroupAction ? action.awardedKarma : null);
  return createPortal(<dialog ref={dialog} className="gw-dialog heroic-approval-dialog" aria-labelledby="heroic-approval-title" onCancel={event => { event.preventDefault(); close(); }}>
    <div className="gw-dialog-content">
      <CheckCircle2 size={44} className="heroic-approval-check mx-auto mb-4" aria-hidden="true" />
      <h2 id="heroic-approval-title" className="text-center">{t('heroic_approval.title')}</h2>
      <p className="heroic-approval-intro text-center mt-2">{t('heroic_approval.message')}</p>
      <div className="heroic-approval-summary mt-5">
        <h3>{action.guildHeroicActionTitle || action.title}</h3>
        {action.performedAt && <p>{t('heroic_approval.performed_at')}: {new Date(action.performedAt).toLocaleDateString(i18n.language)}</p>}
      </div>
      <div className="dashboard-stat-strip is-three-cols heroic-approval-stats">
        <div className="dashboard-stat heroic-approval-stat" style={{ '--stat-color': 'var(--stat-sky, #83d6f1)' }}>
          <CategoryIcon categoryKey={action.categoryKey} className="dashboard-stat-icon" alt="" />
          <strong className="heroic-approval-category">{t(`categories.${action.categoryKey}`, action.categoryKey)}</strong>
          <span className="dashboard-stat-label">{t('heroic_approval.category')}</span>
          <small className="heroic-approval-detail">+{categoryXp.toLocaleString(i18n.language)} {t('heroicActions.categoryXp')}</small>
        </div>
        <div className="dashboard-stat heroic-approval-stat" style={{ '--stat-color': 'var(--stat-amber, #ecd08a)' }}>
          <ExperienceReward key={active.notification.id} amount={xp} locale={i18n.language} icon={ExperienceIcon} caption={presentationText('systemCopy.168aad3e9812')} className="heroic-approval-reward" />
        </div>
        <div className="dashboard-stat heroic-approval-stat" style={{ '--stat-color': 'var(--stat-emerald, #83d5b0)' }}>
          <KarmaIcon className="dashboard-stat-icon" aria-hidden="true" />
          <strong className="dashboard-stat-value">{karma == null ? '—' : <KarmaScoreLink>{`${karma > 0 ? '+' : ''}${karma.toLocaleString(i18n.language)}`}</KarmaScoreLink>}</strong>
          <span className="dashboard-stat-label">{t('heroic_approval.karma')}</span>
        </div>
      </div>
      {milestoneLevels.length > 0 && <section className="heroic-approval-milestones" aria-label={t('heroic_approval.milestones')}>
        <h3>{t('heroic_approval.milestones')}</h3>
        <div className="heroic-approval-milestone-list">{milestoneLevels.map(milestoneLevel =>
          <div className="heroic-approval-milestone" key={milestoneLevel}>
            <CategoryMilestoneBadge categoryKey={action.categoryKey} level={milestoneLevel} label={t('heroic_approval.badge_label', { category: t(`categories.${action.categoryKey}`), level: milestoneLevel })} />
            <div><small>{t('heroic_approval.category_level', { level: milestoneLevel })}</small><strong>{categoryTitle(action.categoryKey, milestoneLevel, i18n.language)}</strong></div>
          </div>)}</div>
      </section>}
    </div>
    <div className="gw-dialog-actions"><button autoFocus className="gw-dialog-primary" onClick={close}>{t('heroic_approval.continue')}</button></div>
  </dialog>, document.body);
}
